// Shared.tsx — YEA GBI Design System Shared Tokens + Primitives
import type { CSSProperties, ReactNode } from 'react';

export const T = {
  // YEA Brand Identity
  brandOrange:  '#E8501A',
  brandAmber:   '#F5A020',
  brandTeal:    '#12B5C4',
  brandBlue:    '#1E7FD4',
  brandPurple:  '#8B3EC8',
  brandGradient:'linear-gradient(90deg, #E8501A 0%, #F5A020 25%, #12B5C4 50%, #1E7FD4 75%, #8B3EC8 100%)',

  // Colors — Light / YEA Brand
  bgVoid:     '#ffffff',
  bgSurface:  '#f8f9fa',
  bgElevated: '#ffffff',
  bgOverlay:  '#f1f3f4',
  bgSubtle:   '#f8f9fa',
  cyan:       '#12B5C4',
  cyanDim:    '#0e8a96',
  yellow:     '#F5A020',
  magenta:    '#E8501A',
  green:      '#188038',
  amber:      '#e37400',
  purple:     '#8B3EC8',
  fgPrimary:  '#202124',
  fgSecondary:'#5f6368',
  fgMuted:    '#9aa0a6',
  border:     '#dadce0',
  borderBright:'#bdc1c6',

  // AI platforms
  chatgpt:    '#10a37f',
  perplexity: '#20b2aa',
  gemini:     '#4285f4',
  grok:       '#0f0f0f',
  gaio:       '#ea4335',  // Google AI Overviews — Google red
  aimode:     '#34a853',  // Google AI Mode — Google green
} as const;

export const sharedStyles = {
  fontDisplay: "'Inter', system-ui, sans-serif",
  fontBody:    "'Inter', system-ui, sans-serif",
  fontMono:    "'JetBrains Mono', monospace",
} as const;

// ── Icon library ──────────────────────────
type IconName =
  | 'dashboard' | 'scan' | 'map' | 'competitor' | 'citation'
  | 'settings' | 'chevronRight' | 'trending' | 'location'
  | 'zap' | 'eye' | 'globe' | 'check' | 'alert' | 'logout';

interface IconProps {
  name: IconName;
  size?: number;
  color?: string;
  style?: CSSProperties;
}

const ICON_PATHS: Record<IconName, string> = {
  dashboard: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z',
  scan:      'M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z',
  map:       'M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 16l4.553 2.276A1 1 0 0021 24.382V8.618a1 1 0 00-.553-.894L15 5m0 15V5m0 0L9 7',
  competitor:'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0',
  citation:  'M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1',
  settings:  'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  chevronRight: 'M9 18l6-6-6-6',
  trending:  'M13 7h8m0 0v8m0-8l-8 8-4-4-6 6',
  location:  'M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z',
  zap:       'M13 2L3 14h9l-1 8 10-12h-9l1-8z',
  eye:       'M15 12a3 3 0 11-6 0 3 3 0 016 0z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z',
  globe:     'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9',
  check:     'M5 13l4 4L19 7',
  alert:     'M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z',
  logout:    'M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1',
};

export function Icon({ name, size = 16, color = T.fgSecondary, style = {} }: IconProps) {
  const d = ICON_PATHS[name] ?? ICON_PATHS.dashboard;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
      stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"
      style={{ flexShrink: 0, ...style }}>
      {d.split(' M').map((segment, i) => (
        <path key={i} d={i === 0 ? segment : 'M' + segment} />
      ))}
    </svg>
  );
}

// ── Shared primitives ─────────────────────────────────────────
export function GBIBadge({ children, color = T.cyan, style = {} }: { children: ReactNode; color?: string; style?: CSSProperties }) {
  const bg = color + '18';
  const border = color + '40';
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 4,
      padding: '2px 10px', borderRadius: 9999,
      fontSize: 11, fontWeight: 600, fontFamily: sharedStyles.fontBody,
      background: bg, color, border: `1px solid ${border}`,
      ...style
    }}>{children}</span>
  );
}

export function GBIDot({ color = T.cyan }: { color?: string }) {
  return <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />;
}

type GlowKey = 'cyan' | 'green' | 'magenta';

export function GBICard({ children, style = {}, glow, onClick }: { children: ReactNode; style?: CSSProperties; glow?: GlowKey; onClick?: () => void }) {
  const glowBorder = glow === 'cyan'    ? 'rgba(26,115,232,0.3)'
                   : glow === 'green'   ? 'rgba(24,128,56,0.3)'
                   : glow === 'magenta' ? 'rgba(217,48,37,0.3)'
                   : T.border;
  const glowShadow = glow === 'cyan'    ? '0 0 0 2px rgba(26,115,232,0.12)'
                   : glow === 'green'   ? '0 0 0 2px rgba(24,128,56,0.1)'
                   : glow === 'magenta' ? '0 0 0 2px rgba(217,48,37,0.1)'
                   : '0 1px 6px rgba(0,0,0,0.08)';
  return (
    <div onClick={onClick} style={{
      background: T.bgElevated,
      border: `1px solid ${glowBorder}`,
      borderRadius: 12,
      padding: 20,
      boxShadow: glowShadow,
      ...style
    }}>{children}</div>
  );
}

export function GBILabel({ children, style = {} }: { children: ReactNode; style?: CSSProperties }) {
  return (
    <div style={{
      fontSize: 10, fontWeight: 700, letterSpacing: '0.1em',
      textTransform: 'uppercase', color: T.fgMuted,
      fontFamily: sharedStyles.fontBody, marginBottom: 6, ...style
    }}>{children}</div>
  );
}

type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'yellow';
type BtnSize = 'sm' | 'md' | 'lg';

export function GBIBtn({ children, variant = 'primary', size = 'md', onClick, style = {}, disabled = false }: {
  children: ReactNode; variant?: BtnVariant; size?: BtnSize;
  onClick?: () => void; style?: CSSProperties; disabled?: boolean;
}) {
  const sizes: Record<BtnSize, CSSProperties> = {
    sm: { fontSize: 12, padding: '6px 12px' },
    md: { fontSize: 13, padding: '9px 18px' },
    lg: { fontSize: 15, padding: '12px 24px' },
  };
  const variants: Record<BtnVariant, CSSProperties> = {
    primary:   { background: 'linear-gradient(90deg,#12B5C4,#1E7FD4)', color: '#fff', border: 'none', boxShadow: '0 2px 8px rgba(18,181,196,0.35)' },
    secondary: { background: '#fff', color: '#12B5C4', border: '1px solid #dadce0', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' },
    ghost:     { background: 'transparent', color: '#12B5C4', border: '1px solid rgba(18,181,196,0.4)' },
    danger:    { background: '#E8501A', color: '#fff', border: 'none' },
    yellow:    { background: '#F5A020', color: '#fff', border: 'none' },
  };
  return (
    <button onClick={onClick} disabled={disabled} style={{
      fontFamily: sharedStyles.fontBody, fontWeight: 600, borderRadius: 6,
      cursor: disabled ? 'not-allowed' : 'pointer',
      display: 'inline-flex', alignItems: 'center', gap: 6,
      transition: 'all 150ms', whiteSpace: 'nowrap',
      opacity: disabled ? 0.5 : 1,
      ...sizes[size], ...variants[variant], ...style
    }}>{children}</button>
  );
}

// Rank bubble colors
export function rankColor(n: number) {
  if (n <= 1) return { bg: '#188038', color: '#fff' };
  if (n <= 3) return { bg: '#34a853', color: '#fff' };
  if (n <= 7) return { bg: '#fbbc04', color: '#202124' };
  if (n <= 9) return { bg: '#e37400', color: '#fff' };
  return { bg: '#d93025', color: '#fff' };
}
